import SERVICE_IDENTIFIER from "../constants/identifiers";
import { Container } from "inversify";
import SystemConfig, { CONFIG_PARAMS, MODES } from "../system/SystemConfig";
import IInitApp from "../engine/IInitApp";
import BaseStates from "../states/BaseStates";
import { STATES } from "../states/states";
import SystemDebug from "../system/SystemDebug";
import { init as initWindowListeners } from "../windowListeners";
import ILoader from "../loader/Iloader";
import IApp from "../engine/IApp";
import SystemTime from "../system/SystemTime";
import CommanderBase from "../commander/CommanderBase";
import SystemInit from "../system/SystemInit";
import Fullscreen from "../ui/screen/Fullscreen";
import AbstractUI from "../ui/AbstractUI";
import IGame from "../game/IGame";
import IStatesManager from "../states/IStatesManager";
import System from "../system/System";
import { createRegulation } from "../regulations/regulationsFactory";
import Settings from "../settings/Settings";
import PlayerOptions from "../player/PlayerOptions";
import Replay from "../replay/Replay";
import Freespins from "../wallet/Freespins";
import PostMessages from "../system/PostMessages";
import events from "../events/EventsObservable";
import { EVENTS } from "../events/baseEvents";
import { IJokerWinBanner } from "../../../src/JokerWinBanner";
import JOKER_BONUS_IDENTIFIERS from "../../../src/JokerWinBanner/src/constants/identifiers";

export async function boot(container: Container) {
	let config = container.get<SystemConfig>(SERVICE_IDENTIFIER.SystemConfig); //init config
	let settings = container.get<Settings>(SERVICE_IDENTIFIER.Settings);

	let debug = container.get<SystemDebug>(SERVICE_IDENTIFIER.SystemDebug);
	debug.init();

	container.get<PostMessages>(SERVICE_IDENTIFIER.PostMessages).init();

	let loader = container.get<ILoader>(SERVICE_IDENTIFIER.EngineLoader);
	await loader.onCreate();

	//createUI
	let ui = container.get<AbstractUI>(SERVICE_IDENTIFIER.UI);
	ui.create();

	let states = container.get<BaseStates>(SERVICE_IDENTIFIER.BaseStates);
	let statesManager = container.get<IStatesManager>(
		SERVICE_IDENTIFIER.StatesManager
	);
	statesManager.register(states);
	statesManager.setState(STATES.INIT);

	let initApp = container.get<IInitApp>(SERVICE_IDENTIFIER.EngineAppInit);
	initApp.init();

	let game = container.get<IGame>(SERVICE_IDENTIFIER.Game);
	let fullscreen = container.get<Fullscreen>(SERVICE_IDENTIFIER.Fullscreen);
	let system = container.get<System>(SERVICE_IDENTIFIER.System);
	initWindowListeners(game, system);

	await loader.init();

	let app = container.get<IApp>(SERVICE_IDENTIFIER.EngineApp);
	await app.init();

	ui.screen.resize();

	await loader.preload();

	let systemInit = container.get<SystemInit>(SERVICE_IDENTIFIER.SystemInit);

	try {
		await systemInit.init(settings);
	} catch (e) {
		console.error(e);
		return;
	}

	let jokerBonus: IJokerWinBanner;

	if (config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY) {
		try {
			jokerBonus = container.get<IJokerWinBanner>(
				JOKER_BONUS_IDENTIFIERS.JOKER_WIN_BANNER_IDENTIFIER
			);
			await jokerBonus.init();
		} catch (e) {
			console.log(e);
			return;
		}
	}

	let systemTime = container.get<SystemTime>(SERVICE_IDENTIFIER.SystemTime);
	systemTime.start();

	let data = await loader.load();

	let playerOptions = container.get<PlayerOptions>(
		SERVICE_IDENTIFIER.PlayerOptions
	);

	let reg = createRegulation(container);
	reg.init();

	try {
		await Promise.all([reg.load(), playerOptions.init()]);
	} catch (e) {
		console.error(e);
		return;
	}

	ui.screen.resize();
	loader.loadComplete(data);

	let commander = container.get<CommanderBase>(SERVICE_IDENTIFIER.Commander);
	commander.create(container);

	await Promise.all([reg.start(), playerOptions.load()]);

	let fs = container.get<Freespins>(SERVICE_IDENTIFIER.Freespins);

	loader.hide();
	events.dispatch(EVENTS.GAME_LOAD_SUCCESS);
	statesManager.setState(STATES.LOADED);

	await statesManager.setState(STATES.SOUND);
	statesManager.setState(STATES.READY);

	await playerOptions.firstSpinButton();

	await commander.runPreinfo();

	fs.init();
	statesManager.setState(STATES.IDLE);

	commander.run();

	try {
		jokerBonus.onGameReady();
	} catch (_) {}

	let replay = container.get<Replay>(SERVICE_IDENTIFIER.Replay);
	await replay.run();
}
